import type {
  IngestMappingConfigSummary,
  TabularConfigBuildDetail,
  TabularConfigBuildPublicReason,
  TabularConfigDraftOverrides
} from '@axilon-apps/domains-ontology-ui-state'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs'
import { ChecksTab } from './tabs/ChecksTab'
import { DescriptionTab } from './tabs/DescriptionTab'
import { FingerprintTab } from './tabs/FingerprintTab'
import { HistoryTab } from './tabs/HistoryTab'
import { PagesTab } from './tabs/PagesTab'
import { ScriptTab } from './tabs/ScriptTab'
import { TablesTab } from './tabs/TablesTab'
import { tabsFor, type ConfigTab } from './util/configView'
import { changedSections, type ChangedSection } from './util/draftChanges'
import type { ConfigSubject } from './util/subject'

const LABEL: Record<ConfigTab, string> = {
  tables: 'Tables',
  checks: 'Checks',
  description: 'Description',
  script: 'Script',
  fingerprint: 'Fingerprint',
  pages: 'Pages',
  history: 'History'
}

const SECTIONS_OF: Partial<Record<ConfigTab, readonly ChangedSection[]>> = {
  tables: ['output', 'pageTables'],
  checks: ['checks'],
  description: ['description', 'agentScripts'],
  script: ['script'],
  fingerprint: ['fingerprint']
}

/**
 * The one tab strip both a saved version and a draft use. Tabs whose section
 * the draft (or History's compare) changed carry a dot.
 */
export function ConfigTabs({
  name,
  subject,
  tab,
  onTabChange,
  navState,
  versions,
  inFlight,
  currentVersion,
  reason,
  edits,
  canManage,
  editable,
  onEditOutput,
  onEditChecks,
  onEditDescription
}: {
  name: string
  subject: ConfigSubject
  tab: ConfigTab
  onTabChange: (tab: ConfigTab) => void
  /** `location.state` to carry on History's links. */
  navState?: unknown
  versions: IngestMappingConfigSummary[]
  inFlight: TabularConfigBuildDetail | null
  currentVersion?: number
  /** The draft's build reason, for the fingerprint tab's file-side diff. */
  reason?: TabularConfigBuildPublicReason
  /** A reviewer's unsaved edits to a draft — the approve call's `overrides`. */
  edits?: TabularConfigDraftOverrides
  canManage: boolean
  /** Whether Checks / Description offer their edit button on this subject. */
  editable: boolean
  onEditOutput?: () => void
  onEditChecks?: () => void
  onEditDescription?: () => void
}) {
  const tabs = tabsFor(subject.parserKind)
  const changed = changedSections(subject.diff)
  const editLabel = subject.kind === 'draft' ? 'Edit on this draft' : 'Edit'
  // A saved agent config's description is its extractor prompt, so editing it
  // starts a build, and the service allows one build per config at a time.
  const descriptionEditable =
    editable && (subject.kind === 'draft' || subject.parserKind === 'script' || inFlight === null)
  return (
    <Tabs
      value={tab}
      onValueChange={(value) => onTabChange(value as ConfigTab)}
      className="min-w-0 gap-3"
    >
      <TabsList
        variant="line"
        className="h-auto w-full flex-wrap justify-start border-b border-line"
      >
        {tabs.map((id) => {
          const dot = (SECTIONS_OF[id] ?? []).some((section) => changed.has(section))
          return (
            <TabsTrigger
              key={id}
              value={id}
              className="h-7 flex-none gap-1.5 px-2.5 text-xs"
              data-testid={`tab-${id}`}
            >
              {LABEL[id]}
              {dot ? (
                <span
                  aria-label="changed"
                  className="size-1.5 rounded-full bg-st-warn"
                  data-testid={`tab-${id}-changed`}
                />
              ) : null}
            </TabsTrigger>
          )
        })}
      </TabsList>
      <TabsContent value="tables">
        <TablesTab
          subject={subject}
          canManage={canManage}
          {...(editable && onEditOutput ? { onEdit: onEditOutput } : {})}
        />
      </TabsContent>
      <TabsContent value="checks">
        <ChecksTab
          subject={subject}
          checks={edits?.checks ?? subject.checks}
          edited={edits?.checks !== undefined}
          canManage={canManage}
          {...(editable ? { editLabel, onEdit: onEditChecks } : {})}
        />
      </TabsContent>
      <TabsContent value="description">
        <DescriptionTab
          subject={subject}
          description={edits?.description ?? subject.description}
          edited={edits?.description !== undefined}
          canManage={canManage}
          {...(descriptionEditable ? { editLabel, onEdit: onEditDescription } : {})}
        />
      </TabsContent>
      {subject.parserKind === 'script' ? (
        <>
          <TabsContent value="script">
            <ScriptTab subject={subject} />
          </TabsContent>
          <TabsContent value="fingerprint">
            <FingerprintTab subject={subject} reason={reason} />
          </TabsContent>
        </>
      ) : (
        <TabsContent value="pages">
          <PagesTab subject={subject} />
        </TabsContent>
      )}
      <TabsContent value="history">
        <HistoryTab
          name={name}
          versions={versions}
          inFlight={inFlight}
          currentVersion={currentVersion}
          navState={navState}
        />
      </TabsContent>
    </Tabs>
  )
}
